/*
 * 这个文件同时导出 Provider 和几个 hook —— 和 components/ui/Toast.tsx 一样，
 * 是「provider + 配套 hook」模块，拆开反而要在两个文件间传 context。
 * Fast Refresh 的这条规则为此关掉。
 */
/* eslint-disable react-refresh/only-export-components */

/**
 * 多语言（中 / 英 / 日）。
 *
 * 词典是**扁平的点号键**：`zh.ts` 是源语言、也是键集合的来源，
 * `en.ts` / `ja.ts` 标成 `Messages` 类型，少一个键或多一个键都过不了
 * `tsc -b`。翻译漏了在构建时就炸，而不是运行时悄悄回退。
 *
 * 非默认语言的词典是**按需加载**的：`main.tsx` 在 render 之前就把选中的
 * 那份 await 进来，所以首屏不会先闪一下中文，React 树里也不需要 loading 态。
 */

import { Fragment, createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import type { MessageKey } from './types'

import { createTranslator } from './translate'
import type { Translator } from './translate'
import { applyDocumentLocale, storeLocale } from './detect'
import type { Locale, Messages } from './types'

export interface I18nValue extends Translator {
  setLocale: (locale: Locale) => Promise<void>
}

const I18nContext = createContext<I18nValue | null>(null)

/** zh 直接打进主包（默认语言），其余两个切走时才拉 */
export async function loadMessages(locale: Locale): Promise<Messages> {
  switch (locale) {
    case 'en':
      return (await import('./locales/en')).en
    case 'ja':
      return (await import('./locales/ja')).ja
    default:
      return (await import('./locales/zh')).zh
  }
}

export function I18nProvider({
  locale: initialLocale,
  messages: initialMessages,
  children,
}: {
  locale: Locale
  messages: Messages
  children: ReactNode
}) {
  const [state, setState] = useState({ locale: initialLocale, messages: initialMessages })

  const setLocale = useCallback(async (next: Locale) => {
    const messages = await loadMessages(next)
    // 先把词典拿到手再切，避免中途出现「键在但翻译还没到」的一帧
    setState({ locale: next, messages })
    storeLocale(next)
    applyDocumentLocale(next)
  }, [])

  const value = useMemo<I18nValue>(
    () => ({ ...createTranslator(state.locale, state.messages), setLocale }),
    [state, setLocale],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext)
  if (!value) throw new Error('useI18n 必须在 I18nProvider 里用')
  return value
}

/** 绝大多数组件只要 t，省一层解构 */
export function useT() {
  return useI18n().t
}

/**
 * 句子中间嵌了元素（链接、加粗的用户名）时用它。
 *
 * 不要拆成「前半句 + 元素 + 后半句」三个键：各语言的语序不一样，
 * 中文的「据称由 X 指示」在英文里是 "Reportedly on instructions from X"，
 * 后半句根本不存在，拆出来的键只能填空字符串。
 *
 *   tx('post.claimedBy', { user: <Link ... /> })
 */
export function useTx() {
  const { t } = useI18n()
  return useCallback(
    (key: MessageKey, nodes: Record<string, ReactNode>): ReactNode => {
      const parts = t(key).split(/(\{\w+\})/g)
      return parts.map((part, index) => {
        const name = /^\{(\w+)\}$/.exec(part)?.[1]
        const node = name ? nodes[name] : undefined
        return <Fragment key={index}>{node ?? (name && name in nodes ? null : part)}</Fragment>
      })
    },
    [t],
  )
}
